import React from "react";
import { motion } from "framer-motion";

const SelectedBuddyInfo = ({ selectedBuddyId }) => {
  // If no buddy is explicitly selected, default to explainer instead of default
  const actualBuddyId = selectedBuddyId || "explainer";

  // Map buddy information with intro messages and example queries for grades 6-10
  const buddyInfo = {
    // default: { // Commented out - using explainer as default now
    //   name: "AI Helper",
    //   icon: "🤖",
    //   description:
    //     "Hi there! I'm your study buddy who can explain homework, answer questions, and help you learn cool new things!",
    //   exampleQueries: [
    //     "Explain photosynthesis in simple words",
    //     "Help me solve 2x + 5 = 15",
    //     "What is the capital of France?",
    //     "Show me how to find the area of a circle",
    //   ],
    // },
    explainer: {
      name: "Concept Explainer",
      icon: "📚",
      description:
        "I provide thorough, step-by-step explanations of any academic concept with examples, diagrams, and real-world applications!",
      exampleQueries: [
        "Explain the water cycle in detail",
        "I need a comprehensive explanation of photosynthesis",
        "Give me an in-depth breakdown of Newton's laws",
        "Explain quadratic equations with examples and applications",
      ],
    },
    blaze: {
      name: "Blaze Sessions",
      icon: "👨‍🏫",
      description:
        "Need extra help with a tough subject? I'll connect you with a real teacher who can explain things just for you!",
      exampleQueries: [
        "I need help with fractions homework",
        "Connect me with a science tutor please",
        "I'm struggling with algebra",
        "Can someone help me with my chemistry questions?",
      ],
    },
    live: {
      name: "Class Finder",
      icon: "🎬",
      description:
        "Want to join fun online classes with other students? I'll help you find awesome classes about topics you like!",
      exampleQueries: [
        "Show me math classes happening this week",
        "Are there any science classes this week?",
        "I want to join a history class",
        "Find physics classes",
      ],
    },
    search: {
      name: "Video Lectures",
      icon: "🎥",
      description:
        "I can find short, helpful videos that make tricky topics super easy to understand - just like your favorite YouTuber!",
      exampleQueries: [
        "Show videos about fractions",
        "Find a video explaining Newton's laws",
        "I need a video about cell biology",
        "Videos that explain algebraic equations",
      ],
    },
    learning: {
      name: "Study Guide",
      icon: "📝",
      description:
        "I'll create a step-by-step guide just for you to master any topic - like having your own personal study plan!",
      exampleQueries: [
        "Create a study plan for understanding cell division",
        "What's the best way to learn about chemical bonding?",
        "Help me understand the digestive system step by step",
        "Make a learning path for quadratic equations",
      ],
    },
    settings: {
      name: "Settings Helper",
      icon: "⚙️",
      description:
        "I can help you change how PuStack looks and works! Change dark or light mode, switch your grade, and more!",
      exampleQueries: [
        "Switch to light mode please",
        "Change my grade to 8th grade",
        "I want to use dark mode",
        "How do I change my profile picture?",
      ],
    },
    navigation: {
      name: "Quick Links",
      icon: "🚀",
      description:
        "I'm like a map for PuStack! I'll help you quickly find exactly what you're looking for in just a few clicks!",
      exampleQueries: [
        "Take me to the math section",
        "Where can I find practice questions?",
        "How do I access my saved notes?",
        "Show me my recent classes",
      ],
    },
  };

  const buddy = buddyInfo[actualBuddyId] || buddyInfo.explainer;

  // Always render the component, even for default buddy
  return (
    <div
      className="mb-4 bg-[#1a1a1a] rounded-lg p-4 border border-[#404040] shadow-lg backdrop-blur-sm"
      style={{
        backgroundColor: "rgba(26, 26, 26, 0.9)", // Semi-transparent for layering
        boxShadow:
          "0 8px 25px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)", // Subtle depth
      }}
    >
      {/* Buddy intro section */}
      <div className="flex items-center mb-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 shadow-lg border border-[#404040]/30"
          style={{
            background:
              actualBuddyId === "blaze"
                ? "linear-gradient(135deg, #f97316 0%, #dc2626 100%)"
                : actualBuddyId === "live"
                ? "linear-gradient(135deg, #22c55e 0%, #059669 100%)"
                : actualBuddyId === "search"
                ? "linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%)"
                : actualBuddyId === "learning"
                ? "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)"
                : actualBuddyId === "settings"
                ? "linear-gradient(135deg, #6b7280 0%, #374151 100%)"
                : actualBuddyId === "navigation"
                ? "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)"
                : actualBuddyId === "explainer"
                ? "linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)"
                : "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.4)", // Subtle shadow instead of blue glow
          }}
        >
          <span className="text-xl drop-shadow-md">{buddy.icon}</span>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-[#ffffff] text-sm font-semibold mb-0.5 flex items-center drop-shadow-md">
            {buddy.name}
            <span className="ml-2 text-[#3ea6ff] text-xs bg-[#3ea6ff]/20 px-2 py-0.5 rounded-full border border-[#3ea6ff]/50 shadow-md">
              Active
            </span>
          </h3>
          <p className="text-[#d7d7d7] text-xs line-clamp-2">
            {buddy.description}
          </p>
        </div>
      </div>

      {/* Example queries section */}
      <div className="space-y-1.5">
        <p className="text-[#d7d7d7] text-xs mb-2 font-medium">
          Try asking me:
        </p>
        <div className="grid grid-cols-2 gap-2">
          {buddy.exampleQueries.map((query, index) => (
            <motion.div
              key={index}
              className="text-xs text-[#ffffff] bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3ea6ff]/30 hover:border-[#3ea6ff]/50 px-3 py-2.5 rounded-lg cursor-pointer flex items-center transition-all duration-200 shadow-md hover:shadow-lg"
              whileHover={{ x: 3, scale: 1.02 }}
              onClick={() => {
                const event = new CustomEvent("assistantFollowUpQuestion", {
                  detail: { question: query },
                });
                window.dispatchEvent(event);
              }}
            >
              <span className="text-[#3ea6ff] mr-2 font-bold drop-shadow-sm">
                •
              </span>
              <span className="line-clamp-1 font-medium">{query}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectedBuddyInfo;
